<template>
  <div id="app" :class="isRiso ? 'riso' : 'legacy'">
    <AppHeader />
    <router-view></router-view>
    <AppFooter />
    <CartModal />
    <CookieConsent />
  </div>
</template>

<script>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useHead } from '@vueuse/head';
import AppHeader from './components/AppHeader.vue';
import AppFooter from './components/AppFooter.vue';
import CartModal from './components/CartModal.vue';
import CookieConsent from './components/CookieConsent.vue'

const SITE_URL = 'https://thinking-of-u.com';
const DEFAULT_TITLE = 'Thinking of U – Send a Real Postcard to Someone You Miss';
const DEFAULT_DESCRIPTION = 'Send a one-of-a-kind postcard to someone you care about. You choose the vibe, we deliver the magic. No previews. No edits. Just a little surprise.';
const OG_IMAGE = `${SITE_URL}/og-image.png`;

export default {
  name: 'App',
  components: {
    CookieConsent,
    AppHeader,
    AppFooter,
    CartModal
  },
  setup() {
    const route = useRoute();
    // A page that has been redrawn in the riso system opts in with route meta
    // `riso: true`; the rest keep the old look until S15 reaches them.
    const isRiso = computed(() => Boolean(route.meta && route.meta.riso));

    // Per-route title/description/canonical + Open Graph / Twitter tags, driven by
    // route.meta. The build-time prerender (vue.config.js) captures these into each
    // public route's served HTML, so raw-HTML crawlers and social unfurlers get the
    // real per-route tags rather than the static fallback in index.html.
    useHead(computed(() => {
      const title = route.meta && route.meta.title ? route.meta.title : DEFAULT_TITLE;
      const description = route.meta && route.meta.description ? route.meta.description : DEFAULT_DESCRIPTION;
      // Self-referential canonical, trailing slash normalized off (except root).
      const path = route.path === '/' ? '/' : route.path.replace(/\/+$/, '');
      const canonical = SITE_URL + path;
      const robots = route.meta && route.meta.noindex ? 'noindex, follow' : 'index, follow';
      return {
        title,
        meta: [
          { name: 'description', content: description },
          { name: 'robots', content: robots },
          { property: 'og:title', content: title },
          { property: 'og:description', content: description },
          { property: 'og:type', content: 'website' },
          { property: 'og:url', content: canonical },
          { property: 'og:image', content: OG_IMAGE },
          { property: 'og:image:width', content: '512' },
          { property: 'og:image:height', content: '512' },
          { property: 'og:site_name', content: 'Thinking of U' },
          // og-image is a 512x512 square logo → 'summary' (square) card. Swap to a
          // 1200x630 image + 'summary_large_image' when a purpose-built OG image exists.
          { name: 'twitter:card', content: 'summary' },
          { name: 'twitter:title', content: title },
          { name: 'twitter:description', content: description },
          { name: 'twitter:image', content: OG_IMAGE },
        ],
        link: [{ rel: 'canonical', href: canonical }],
      };
    }));

    // Valid site-wide Organization schema (replaces the old WebSite JSON-LD whose
    // SearchAction pointed at a non-existent /search page).
    useHead({
      script: [{
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Thinking of U',
          url: SITE_URL,
          logo: OG_IMAGE,
          description: DEFAULT_DESCRIPTION,
        }),
      }],
    });

    return { isRiso };
  },
  created() {
    const params = new URLSearchParams(window.location.search);
    const testModeKey = params.get('testmode');
    if (testModeKey) {
      sessionStorage.setItem('testModeKey', testModeKey);
    }
  }
};
</script>

<style>
#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  max-width: 100vw;
  /* clip, not hidden: `hidden` makes #app a scroll container, which quietly
     breaks every position: sticky inside it. */
  overflow-x: clip;
}

router-view {
  flex: 1;
}

/* The old look, for pages not yet redrawn: pink, centred, Avenir. The riso
   pages get theirs from assets/riso.css (.riso). */
#app.legacy {
  font-family: 'Avenir', Helvetica, Arial, sans-serif;
  text-align: center;
  color: #e41393;
}
</style>
